import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiCallError, type DigestSummary } from '../lib/api';
import { useAuth } from '../lib/auth';

function formatWeekEnd(weekEnd: string): string {
  const date = new Date(`${weekEnd}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return weekEnd;
  return date.toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

function formatRelative(isoString: string): string {
  const ts = new Date(isoString).getTime();
  if (Number.isNaN(ts)) return isoString;
  const sec = Math.max(0, Math.round((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 14) return `${day}d ago`;
  return isoString.slice(0, 10);
}

export function Digests(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.idToken;

  const [digests, setDigests] = useState<readonly DigestSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (idToken === null) return;
      setLoading(true);
      try {
        const result = await api.listDigests(idToken, signal);
        setDigests(result.digests);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiCallError ? err.message : 'failed to load digests');
      } finally {
        setLoading(false);
      }
    },
    [idToken],
  );

  useEffect(() => {
    const ac = new AbortController();
    void load(ac.signal);
    return () => ac.abort();
  }, [load]);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-baseline justify-between mb-4">
        <h1 className="font-mono text-lg uppercase tracking-wider text-ink">Past Digests</h1>
        <span className="text-2xs font-mono text-ink-muted">
          {digests.length} {digests.length === 1 ? 'digest' : 'digests'}
        </span>
      </div>

      {error !== null && (
        <div className="border border-rose-500/40 bg-rose-500/10 text-rose-300 px-3 py-2 mb-4 text-xs font-mono">
          {error}
        </div>
      )}

      {digests.length === 0 && !loading && error === null && (
        <div className="text-center py-12 text-sm font-mono text-ink-muted">
          No digests yet. The next weekly run will populate this list.
        </div>
      )}

      {digests.length === 0 && loading && (
        <div className="text-center py-12 text-2xs font-mono text-ink-muted">…</div>
      )}

      <ul className="space-y-3">
        {digests.map((digest) => (
          <li key={digest.s3_key}>
            <Link
              to={`/digests/${digest.week_end}`}
              className="block bg-bg-surface border border-bg-line hover:border-accent/40 hover:bg-bg-raised transition-colors p-4"
            >
              <div className="flex items-baseline justify-between gap-4">
                <h2 className="text-sm font-mono text-ink">{formatWeekEnd(digest.week_end)}</h2>
                <span className="text-2xs font-mono text-ink-muted">{formatRelative(digest.last_modified)}</span>
              </div>
              <div className="mt-2 text-2xs font-mono text-ink-muted">
                {digest.week_end} · {formatBytes(digest.size_bytes)}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
